import {
	EmailTemplate,
	EmailTemplateMappings,
	RelatedEntityType,
} from "@dune/types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	Badge,
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@dune/ui";
import { Copy, Edit, Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDeleteEmailTemplate } from "../../../../../api/mutations/email-templates.mutations";
import EmailTemplatesTabSkeleton from "../skeletons/EmailTemplatesTabSkeleton";

interface EmailTemplatesTabContentProps {
	isLoading: boolean;
	error?: Error | null;
	templates?: EmailTemplate[];
	mappings?: EmailTemplateMappings;
	isLoadingMappings: boolean;
	errorMappings?: Error | null;
}

const getEntityLabel = (entityModel: RelatedEntityType): string => {
	switch (entityModel) {
		case RelatedEntityType.BROKER:
			return "Courtier";
		case RelatedEntityType.CONSTRUCTION_SITE:
			return "Chantier";
		case RelatedEntityType.PROPOSITION:
			return "Proposition";
		default:
			return entityModel;
	}
};

const EmailTemplatesTabContent: React.FC<EmailTemplatesTabContentProps> = ({
	isLoading,
	error,
	templates,
	isLoadingMappings,
}) => {
	const navigate = useNavigate();
	const deleteTemplate = useDeleteEmailTemplate();
	const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(
		null,
	);

	const handleCreateTemplate = () => {
		navigate("/email-templates/create");
	};

	const handleEditTemplate = (id: string) => {
		navigate(`/email-templates/edit/${id}`);
	};

	const handleCopyTemplate = async (template: EmailTemplate) => {
		try {
			// Create a copy with new name
			const copyName = `${template.name} (Copie)`;
			navigate(
				`/email-templates/create?copyFrom=${template.id}&name=${encodeURIComponent(copyName)}`,
			);
		} catch {
			toast.error("Erreur lors de la copie du template");
		}
	};

	const handleDeleteTemplate = async (id: string) => {
		setDeletingTemplateId(id);
		try {
			await deleteTemplate.mutateAsync(id);
			toast.success("Template supprimé avec succès");
		} catch {
			toast.error("Erreur lors de la suppression du template");
		} finally {
			setDeletingTemplateId(null);
		}
	};

	if (isLoading || isLoadingMappings) {
		return <EmailTemplatesTabSkeleton />;
	}

	if (error) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-2xl font-semibold text-foreground">
							Modèles d'emails
						</h2>
						<p className="text-muted-foreground mt-1">
							Gérez vos templates d'emails avec des placeholders dynamiques
						</p>
					</div>
					<Button
						onClick={handleCreateTemplate}
						className="flex items-center gap-2"
					>
						<Plus className="w-4 h-4" />
						Créer un nouveau modèle
					</Button>
				</div>
				<p className="text-red-500">Erreur : {error.message}</p>
			</div>
		);
	}

	if (!templates || templates.length === 0) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-2xl font-semibold text-foreground">
							Modèles d'emails
						</h2>
						<p className="text-muted-foreground mt-1">
							Gérez vos templates d'emails avec des placeholders dynamiques
						</p>
					</div>
					<Button
						onClick={handleCreateTemplate}
						className="flex items-center gap-2"
					>
						<Plus className="w-4 h-4" />
						Créer un nouveau modèle
					</Button>
				</div>

				<div className="text-center py-12">
					<div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
						<Plus className="w-12 h-12 text-muted-foreground" />
					</div>
					<h3 className="text-lg font-medium text-foreground mb-2">
						Aucun modèle d'email
					</h3>
					<p className="text-muted-foreground">
						Commencez par créer votre premier modèle d'email.
					</p>
				</div>
				<div className="flex justify-center">
					<Button
						onClick={handleCreateTemplate}
						className="flex items-center gap-2"
					>
						<Plus className="w-4 h-4" />
						Créer un nouveau modèle
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl font-semibold text-foreground">
						Modèles d'emails
					</h2>
					<p className="text-muted-foreground mt-1">
						Gérez vos templates d'emails avec des placeholders dynamiques
					</p>
				</div>
				<Button
					onClick={handleCreateTemplate}
					className="flex items-center gap-2"
				>
					<Plus className="w-4 h-4" />
					Créer un nouveau modèle
				</Button>
			</div>

			{templates && templates.length > 0 && (
				<div className="border rounded-lg">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Nom</TableHead>
								<TableHead>Entité</TableHead>
								<TableHead>Sujet</TableHead>
								<TableHead>Statut</TableHead>
								<TableHead>Dernière modification</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{templates.map((template) => (
								<TableRow key={template.id}>
									<TableCell className="font-medium">{template.name}</TableCell>
									<TableCell>
										<Badge variant="outline">
											{getEntityLabel(template.entityModel)}
										</Badge>
									</TableCell>
									<TableCell
										className="max-w-xs truncate"
										title={template.subject}
									>
										{template.subject}
									</TableCell>
									<TableCell>
										<div className="flex items-center gap-2">
											{template.validated ? (
												<Badge variant="default">Valide</Badge>
											) : (
												<Tooltip>
													<TooltipTrigger>
														<Badge variant="destructive">Invalide</Badge>
													</TooltipTrigger>
													<TooltipContent>
														<div className="max-w-xs">
															<p className="font-medium mb-1">
																Champs invalides:
															</p>
															<ul className="text-xs">
																{template.invalidFields.map((field) => (
																	<li key={field}>• {field}</li>
																))}
															</ul>
														</div>
													</TooltipContent>
												</Tooltip>
											)}
										</div>
									</TableCell>
									<TableCell>
										{new Date(template.updatedAt).toLocaleDateString("fr-FR", {
											day: "2-digit",
											month: "2-digit",
											year: "numeric",
											hour: "2-digit",
											minute: "2-digit",
										})}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex items-center justify-end gap-1">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => handleEditTemplate(template.id)}
												title="Modifier le template"
											>
												<Edit className="w-4 h-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => handleCopyTemplate(template)}
												title="Créer une copie de ce template"
											>
												<Copy className="w-4 h-4" />
											</Button>
											<AlertDialog>
												<AlertDialogTrigger asChild>
													<Button
														variant="ghost"
														size="icon"
														title="Supprimer"
														className="text-destructive hover:text-destructive"
													>
														<Trash2 className="w-4 h-4" />
													</Button>
												</AlertDialogTrigger>
												<AlertDialogContent>
													<AlertDialogHeader>
														<AlertDialogTitle>
															Supprimer le template
														</AlertDialogTitle>
														<AlertDialogDescription>
															Êtes-vous sûr de vouloir supprimer le template "
															{template.name}" ? Cette action est irréversible.
														</AlertDialogDescription>
													</AlertDialogHeader>
													<AlertDialogFooter>
														<AlertDialogCancel>Annuler</AlertDialogCancel>
														<AlertDialogAction
															variant="destructive"
															onClick={() => handleDeleteTemplate(template.id)}
															disabled={deletingTemplateId === template.id}
														>
															{deletingTemplateId === template.id
																? "Suppression..."
																: "Supprimer"}
														</AlertDialogAction>
													</AlertDialogFooter>
												</AlertDialogContent>
											</AlertDialog>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
};

export default EmailTemplatesTabContent;
